import { observer } from 'src/utils/mobx-react';
import React, { useState } from 'react';
import { useAppStateContext } from 'src/AppState/AppState';
import Loading from 'src/components/Loading';
import { DateTime } from 'src_common/utils/time/time';
import { Button } from 'src/domains/layouts/shared/buttons/Button';
import { Spinner } from 'src/assets/icons/Spinner';
import {
    AuditIdMissionTable,
    AuditIdMissionContainer,
    AuditIdMissionHeader,
    AuditIdMissionTitle,
    AuditIdMissionWrapper,
    CloseButton,
    JsonDetailsRow,
    JsonDetailsContainer,
    JsonDetailsTitle,
    JsonDetailsContent,
    ActionsContainer,
    ButtonWrapper,
    ButtonLoadingOverlay,
    SpinnerContainer,
} from './AuditIdMission.style';
import { AuditIdMissionState } from './AuditIdMission.state';
import { useCommon } from 'src/AppState/Common';

const AuditIdMissionBody = observer('AuditIdMissionBody', () => {
    const { routerBackoffice } = useAppStateContext();
    const common = useCommon();
    const [state] = useState(() => new AuditIdMissionState(routerBackoffice, common));

    return (
        <div className='visible audits'>
            <div className='audits-content'>
                <CloseButton
                    className='audits-minimize'
                    onClick={(): void => {
                        routerBackoffice.removeParamInNamespace('customers', 'audits');
                    }}
                    data-test='audit-id-mission-close-button'
                />
                <AuditIdMissionWrapper>
                    <AuditIdMissionHeader>
                        <AuditIdMissionTitle data-test='audit-id-mission-title'>EKYC Panel</AuditIdMissionTitle>
                    </AuditIdMissionHeader>

                    <Loading
                        message='Loading audits...'
                        active={state.status === 'loading'}
                    />

                    {state.status === 'error' ? (
                        <AuditIdMissionContainer>Error occurred while loading data</AuditIdMissionContainer>
                    ) : (
                        <AuditIdMissionContainer data-test='audit-id-mission-container'>
                            <AuditIdMissionTable data-test='audit-id-mission-table'>
                                <thead>
                                    <tr>
                                        <th data-test='type-of-check-column-title'>Type of Check</th>
                                        <th data-test='date-started-column-title'>Date Started</th>
                                        <th data-test='date-finalized-column-title'>Date Finalized</th>
                                        <th data-test='verifications-progress-column-title'>Verifications Progress</th>
                                        <th data-test='verification-status-message-column-title'>
                                            Verification Status Message
                                        </th>
                                        <th data-test='request-id-column-title'>Request ID</th>
                                        <th data-test='trace-id-column-title'>Trace ID</th>
                                        <th data-test='ip-address-column-title'>IP Address</th>
                                        <th data-test='actions-column-title'>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {state.status === 'noData' ? (
                                        <tr>
                                            <td colSpan={9}>No results found</td>
                                        </tr>
                                    ) : (
                                        state.auditData.map((item) => {
                                            const rowKey = item.verificationId.toString();
                                            return (
                                                <React.Fragment key={item.verificationId}>
                                                    <tr>
                                                        <td data-test={`type-of-check-${item.verificationId}`}>
                                                            {item.typeOfCheck}
                                                        </td>
                                                        <td data-test={`date-started-${item.verificationId}`}>
                                                            {DateTime.from(item.dateCreated)?.format(
                                                                'YYYY-MM-DD HH:mm'
                                                            )}
                                                        </td>
                                                        <td data-test={`date-finalized-${item.verificationId}`}>
                                                            {item.dateFinalized === '--'
                                                                ? '--'
                                                                : DateTime.from(item.dateFinalized)?.format(
                                                                      'YYYY-MM-DD HH:mm'
                                                                  )}
                                                        </td>
                                                        <td data-test={`internal-status-${item.verificationId}`}>
                                                            {item.internalStatus}
                                                        </td>
                                                        <td
                                                            data-test={`verification-status-message-${item.verificationId}`}
                                                        >
                                                            {item.statusMessage}
                                                        </td>
                                                        <td data-test={`request-id-${item.verificationId}`}>
                                                            {item.requestId}
                                                        </td>
                                                        <td data-test={`trace-id-${item.verificationId}`}>
                                                            {item.traceId}
                                                        </td>
                                                        <td data-test={`ip-address-${item.verificationId}`}>
                                                            {item.ipAddress}
                                                        </td>
                                                        <td>
                                                            <ActionsContainer>
                                                                <Button
                                                                    version='primary'
                                                                    size='regular'
                                                                    onClick={() =>
                                                                        state.getAuditIdMissionDataJson(
                                                                            item.verificationId
                                                                        )
                                                                    }
                                                                    dataTest={`view-details-${item.verificationId}`}
                                                                >
                                                                    {state.expandedRowId === rowKey
                                                                        ? 'Hide Details'
                                                                        : 'View Details'}
                                                                </Button>
                                                                {item.internalStatus === 'requested' && (
                                                                    <ButtonWrapper>
                                                                        <Button
                                                                            version='secondary'
                                                                            size='regular'
                                                                            onClick={() =>
                                                                                state.cancelVerification(
                                                                                    item.verificationId
                                                                                )
                                                                            }
                                                                            isDisabled={
                                                                                state.cancelingVerificationId ===
                                                                                item.verificationId
                                                                            }
                                                                            dataTest={`cancel-verification-${item.verificationId}`}
                                                                        >
                                                                            Cancel
                                                                        </Button>
                                                                        <ButtonLoadingOverlay
                                                                            isLoading={
                                                                                state.cancelingVerificationId ===
                                                                                item.verificationId
                                                                            }
                                                                        >
                                                                            <SpinnerContainer>
                                                                                <Spinner type='basic' />
                                                                            </SpinnerContainer>
                                                                        </ButtonLoadingOverlay>
                                                                    </ButtonWrapper>
                                                                )}
                                                            </ActionsContainer>
                                                        </td>
                                                    </tr>
                                                    {state.expandedRowId === rowKey &&
                                                        state.selectedAuditJson !== null && (
                                                            <tr>
                                                                <JsonDetailsRow colSpan={9}>
                                                                    <JsonDetailsContainer data-test='json-details-container'>
                                                                        <JsonDetailsTitle data-test='json-details-title'>
                                                                            JSON Details:
                                                                        </JsonDetailsTitle>
                                                                        <JsonDetailsContent data-test='json-details-content'>
                                                                            {state.selectedAuditJson.data.jsonResponse}
                                                                        </JsonDetailsContent>
                                                                    </JsonDetailsContainer>
                                                                </JsonDetailsRow>
                                                            </tr>
                                                        )}
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                </tbody>
                            </AuditIdMissionTable>
                        </AuditIdMissionContainer>
                    )}
                </AuditIdMissionWrapper>
            </div>
        </div>
    );
});

export const AuditIdMission = observer('AuditIdMission', () => {
    const { routerBackoffice } = useAppStateContext();
    const isOpened = routerBackoffice.getParametersString('customers', 'audits') === 'idMissionAudit';

    return isOpened ? <AuditIdMissionBody /> : null;
});
